import { useState } from "react";
import { useAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getRouteStops } from "@/api/routes";
import { formatDateOnly } from "@/lib/format";
import {
  describeBuildWarning,
  MATRIX_ESTIMATED,
} from "@/features/routes/lib/buildWarnings";
import { builtRoutesAtom, builtRoutesByDateAtom } from "@/store/buildRouteTask";
import type { BuildDateSummary, BuiltRoute, RouteStop } from "@/types/routes";

/** Lazily fetches and renders the order stops of a single expanded route row. */
function RouteStopsList({ routeId }: { routeId: number }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["route-stops", routeId],
    queryFn: () => getRouteStops(routeId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600">
        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
        {t("routes.buildSummary.loading")}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="px-3 py-2 text-xs text-gray-500">
        {t("routes.buildSummary.loadError")}
      </p>
    );
  }

  // Only order stops carry order_title/order_address; depot stops are skipped.
  const orderStops = data.filter(
    (stop: RouteStop) => stop.stop_type === "order",
  );

  if (orderStops.length === 0) {
    return (
      <p className="px-3 py-2 text-xs text-gray-500">
        {t("routes.buildSummary.noOrders")}
      </p>
    );
  }

  function getOrderLabel(stop: RouteStop): string {
    if (stop.order_title) return stop.order_title;
    if (stop.order_external_id) return `#${stop.order_external_id}`;
    if (stop.order_address) return stop.order_address;
    return `#${stop.order_id ?? stop.id}`;
  }

  return (
    <ul className="space-y-0.5 px-3 pb-2 pt-1">
      {orderStops.map((stop) => (
        <li key={stop.id} className="text-xs text-gray-600">
          {getOrderLabel(stop)}
          {stop.order_address && stop.order_title && (
            <span className="ml-1 text-brand-success">
              — {stop.order_address}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A single accordion row: route header + on-demand order list when expanded. */
function RouteRow({ route }: { route: BuiltRoute }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="overflow-hidden rounded-lg border border-brand-success/30 bg-white">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-brand-success/5"
      >
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-gray-900">
            {route.vehicle?.name ?? route.name}
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            {t("routes.buildSummary.routeMeta", {
              km: route.planned_mileage_km ?? 0,
              min: route.planned_duration_min ?? 0,
            })}
            {" · "}
            {t("routes.buildSummary.orderCount", { count: route.stops_count })}
            {/* BUG-691: km/min above are a straight-line estimate. */}
            {route.eta_estimated ? (
              <span
                data-testid="eta-estimated"
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand-warning/15 px-1.5 py-px font-medium text-gray-800"
              >
                <AlertTriangle
                  size={11}
                  className="text-brand-warning"
                  aria-hidden="true"
                />
                {t("routes.buildWarnings.etaEstimatedBadge")}
              </span>
            ) : null}
          </p>
        </div>
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`shrink-0 text-brand-success transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="border-t border-brand-success/20 bg-brand-success/5">
          <RouteStopsList routeId={route.id} />
        </div>
      )}
    </div>
  );
}

/**
 * Compact per-delivery-date breakdown shown directly under the header. Rendered
 * only when it adds information: multiple date groups, or an overdue pull-forward
 * onto today. One line per date: formatted date, route + order counts, and — for
 * the today-group — an italic note about overdue orders built for today.
 */
function BuildDateBreakdown({ byDate }: { byDate: BuildDateSummary[] }) {
  const { t, i18n } = useTranslation();

  return (
    <div className="mb-3">
      <p className="mb-1 text-xs font-semibold text-gray-900">
        {t("routes.buildSummary.byDateTitle")}
      </p>
      <ul className="space-y-0.5">
        {byDate.map((entry) => (
          <li key={entry.date} className="text-xs text-gray-600">
            <span className="font-medium text-gray-900">
              {formatDateOnly(entry.date, i18n.language)}
            </span>
            {" — "}
            {t("routes.buildSummary.routeCount", { count: entry.route_count })}
            {" · "}
            {/* Review round 2: `order_count` is what was PLACED; when fewer than
                the picks of that date, the picks are named too. */}
            {entry.picked_count !== undefined &&
            entry.picked_count > entry.order_count
              ? t("routes.buildSummary.orderCountOfPicked", {
                  orders: t("routes.buildSummary.orderCount", {
                    count: entry.order_count,
                  }),
                  picked: entry.picked_count,
                })
              : t("routes.buildSummary.orderCount", {
                  count: entry.order_count,
                })}
            {entry.overdue_order_count > 0 && (
              <span className="ml-1 italic text-gray-500">
                (
                {t("routes.buildSummary.overdueNote", {
                  count: entry.overdue_order_count,
                })}
                )
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Dismissible green summary panel rendered after a successful build when at
 * least one route was created. Lists every created route as an accordion row
 * (vehicle, distance, duration, order count); expanding a row lazily fetches
 * and shows that route's orders.
 *
 * Reads from `builtRoutesAtom`; clears the atom on dismiss.
 * Renders nothing when the atom is empty.
 */
export function BuildRoutesSummaryAlert() {
  const { t } = useTranslation();
  const [routes, setRoutes] = useAtom(builtRoutesAtom);
  const [byDate, setByDate] = useAtom(builtRoutesByDateAtom);

  if (routes.length === 0) return null;

  function handleDismiss() {
    setRoutes([]);
    setByDate([]);
  }

  // Only show the date breakdown when it adds information: more than one
  // delivery-date group, or overdue orders pulled forward onto today.
  const showByDate =
    byDate.length > 1 || byDate.some((entry) => entry.overdue_order_count > 0);

  // BUG-691: the same sentence the build toast shows, for the routes flagged here.
  const estimatedCount = routes.filter((route) => route.eta_estimated).length;

  return (
    <div
      role="status"
      className="rounded-xl border border-brand-success/30 bg-brand-success/5 px-5 py-4 shadow-sm"
    >
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2
            size={18}
            className="mt-0.5 shrink-0 text-brand-success"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-gray-900">
            {t("routes.buildSummary.title")}
            <span className="ml-2 font-normal text-gray-600">
              {t("routes.buildSummary.routeCount", { count: routes.length })}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("routes.buildSummary.dismiss")}
          className="rounded p-0.5 text-brand-success transition-colors hover:bg-brand-success/10 hover:text-gray-700"
        >
          <X size={16} />
        </button>
      </div>

      {estimatedCount > 0 && (
        <p
          role="note"
          className="mb-3 flex items-start gap-2 rounded-lg border border-brand-warning/40 bg-brand-warning/10 px-3 py-2 text-xs text-gray-800"
        >
          <AlertTriangle
            size={14}
            className="mt-px shrink-0 text-brand-warning"
            aria-hidden="true"
          />
          {describeBuildWarning(
            {
              code: MATRIX_ESTIMATED,
              params: { route_count: estimatedCount },
            },
            t,
          )}
        </p>
      )}

      {/* Per-delivery-date breakdown (only when it adds information) */}
      {showByDate && <BuildDateBreakdown byDate={byDate} />}

      {/* Accordion rows */}
      <div className="space-y-2">
        {routes.map((route) => (
          <RouteRow key={route.id} route={route} />
        ))}
      </div>
    </div>
  );
}
